import { isDate } from '@js/utils/date';

export function formatNumber(number, withDecimals = true, withPlus = false) {
    if (typeof number !== 'number') {
        return '';
    }

    if (!withDecimals) {
        if (withPlus) {
            return number > 0
                ? '+' + number.toFixed(0).replace(/\B(?=(\d{3})+(?!\d))/g, ' ')
                : number.toFixed(0).replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
        }
        return number.toFixed(0).replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
    }

    if (withPlus) {
        return number > 0
            ? '+' + number.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ' ')
            : number.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
    }

    return number.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
}

export function formatPercentage(
    percentage,
    withPlus = false,
    withDecimal = false,
) {
    if (typeof percentage !== 'number') {
        return '';
    }

    const formattedPercentage = withDecimal
        ? percentage.toFixed(2) + ' %'
        : Math.round(percentage) + ' %';

    if (withPlus) {
        return percentage > 0 ? '+' + formattedPercentage : formattedPercentage;
    }

    return formattedPercentage;
}

const CURRENCY_SYMBOLS = {
    EUR: '€',
    USD: '$',
    GBP: '£',
};

/**
 * @returns {String} The symbol for an ISO code, or the code itself when there is no symbol for it
 */
export function currencySymbol(code) {
    return CURRENCY_SYMBOLS[code] ?? code ?? '€';
}

export function formatPrice(
    value,
    withDecimals = true,
    spaceSize = 1,
    currency = '€',
) {
    let price = parseFloat(value).toFixed(2);

    let spaces = '';
    for (let i = 0; i < spaceSize; i++) {
        spaces += '\xa0';
    }

    let numerals = price.split('.')[0].replace(/\B(?=(\d{3})+(?!\d))/g, spaces);
    let decimals = price.split('.')[1];

    if (!withDecimals) {
        return numerals + ' ' + currency;
    }

    return numerals + ',' + decimals + ' ' + currency;
}

export const formatTime = (hours, minutes) => {
    let formattedMinutes;

    if (typeof minutes === 'string') {
        formattedMinutes = minutes.padEnd(2, '0');
    } else {
        formattedMinutes = minutes.toFixed(0).padEnd(2, '0');
    }

    if (hours) {
        return hours + 'h' + formattedMinutes;
    } else if (minutes && minutes !== '00') {
        return formattedMinutes + 'min';
    } else {
        return '';
    }
};

export const formatDate = (
    date,
    isTimestamp = false,
    withTime = false,
    withDay = true,
    withMonth = true,
    withYear = true,
) => {
    if (isTimestamp) {
        date = new Date(date * 1000);
    }

    if (!(date instanceof Date)) {
        date = new Date(date);
    }

    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const year = date.getFullYear();

    const hours = date.getHours().toString().padStart(2, '0');
    const minutes = date.getMinutes().toString().padStart(2, '0');

    let formattedDate = '';

    if (withDay) {
        formattedDate += day + '/';
    }

    if (withMonth) {
        formattedDate += month + '/';
    }

    if (withYear) {
        formattedDate += year;
    }

    if (withTime) {
        formattedDate += ' ' + hours + ':' + minutes;
    }

    return formattedDate;
};

export const formatPhoneNumber = (number) => {
    if (!number) {
        return number;
    }

    let digits = number.replace(/\D/g, '');

    if (digits.startsWith('00')) {
        digits = digits.slice(2);
    }

    if (digits.startsWith('33') && digits.length === 11) {
        digits = '0' + digits.slice(2);
    }

    if (digits.length !== 10) {
        return number;
    }

    return digits.match(/.{2}/g).join(' ');
};

export const formatDateForCookies = (date) => {
    return !isDate(date)
        ? date
        : [
              date.getFullYear(),
              String(date.getMonth() + 1).padStart(2, '0'),
              String(date.getDate()).padStart(2, '0'),
          ].join('-');
};
